/**
 * GradeFAQ — accordion FAQ for the /grade landing page. Uses native
 * <details>/<summary> for a11y + zero JS. Items are intentionally
 * commercial-positioning ("how accurate?", "is my image private?",
 * "should I send to PSA?") to address the questions a serious grader
 * has before they trust a free tool.
 */
import React from 'react';

const ITEMS = [
  {
    q: 'How accurate is the AI grade?',
    a: (
      <>
        We grade on the same four sub-scales PSA uses (centering, corners, edges, surface)
        and aggregate them with PSA's published cap-by-min-subgrade rule. Centering is
        measured to ±0.5% pixel precision; corners, edges, and surface are heuristic
        scorers continuously calibrated against the real PSA/BGS/CGC/AGS grades our
        users report back. The "real-grade comparisons" counter above is the live
        sample count — the more you submit, the tighter the calibration gets.
      </>
    ),
  },
  {
    q: 'Will the AI guarantee my card gets the same grade from PSA?',
    a: (
      <>
        No — and any service that says it will is lying. We give you a confident
        pre-grade based on what's visible in your photos. Real graders use higher-end
        equipment, multi-light photometric capture, and human review. Treat our score
        as a strong indicator of which tier your card is likely in, and use the
        "Worth grading?" calculator to decide if it's worth the submission fee.
      </>
    ),
  },
  {
    q: 'Is my photo or card information private?',
    a: (
      <>
        Photos are stored on our server only so you can revisit your report and share
        it via a link. Anonymous submissions never link to a user account. If you sign
        in, your reports go into your private grading history. We don't sell or share
        your images with anyone, ever.
      </>
    ),
  },
  {
    q: 'What cards does it support?',
    a: (
      <>
        Today: Pokémon TCG (modern + vintage). The centering / corners / edges /
        surface analysis works for any rectangular card, but identification + reference
        comparisons are Pokémon-only at launch. Sports cards, MTG, Yu-Gi-Oh, and
        Lorcana support are on the roadmap.
      </>
    ),
  },
  {
    q: 'Can I use it without a phone?',
    a: (
      <>
        Yes — any device with a webcam works (laptop, desktop, tablet). If you don't
        have a camera handy, switch to file upload and submit two photos directly.
        Phone capture is just easier because the rear camera tends to give sharper,
        better-framed shots.
      </>
    ),
  },
  {
    q: 'How is this free?',
    a: (
      <>
        SilverGames is a Canadian Pokémon TCG retailer — the grader is part of how we
        serve our community. The compute runs on infrastructure we already own, so
        it costs us nothing extra to offer. There is no paywall, no premium tier, and
        no rate-limited "trial."
      </>
    ),
  },
  {
    q: 'Do you have an app?',
    a: (
      <>
        Not a native app — the web version works exactly like a native one. Tap "Add to
        home screen" in your browser's share menu and you'll have a one-tap-to-grade
        icon on your phone, no App Store required.
      </>
    ),
  },
];

export default function GradeFAQ() {
  return (
    <section className="gp-section">
      <span className="gp-eyebrow">FAQ</span>
      <h2 className="gp-h2">Common questions</h2>
      <div>
        {ITEMS.map((item, i) => (
          <details key={i} className="gp-faq__item">
            <summary className="gp-faq__q">{item.q}</summary>
            <div className="gp-faq__a">{item.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}
